import { useCallback, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import qz from "qz-tray";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useQzPrint } from "src/hooks/useQzPrint";
import { Tab } from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { validateString } from "src/utils/string/stringUtil";
import {
	showSuccess,
	ShowSuccessFetch,
	showWarning,
} from "src/utils/toast/toastUtil";
import {
	downloadExcel_TQC2100_R01,
	getTQC2100_R01AllCount,
	getTQC2100_R01DetailList,
	getTQC2100_R01InspectList,
	getTQC2100_R01List,
	getPdfBlob_TQC2100_R01,
	patchTQC2100_R01,
	postTQC2100_R01,
} from "./Api_TQC2100_R01";
import {
	defaultTQC2100_R01HeaderDto,
	TQC2100_R01DetailDto,
	TQC2100_R01DetailPivotDto,
	TQC2100_R01HeaderDto,
	TQC2100_R01InspectMasterDto,
} from "./Dto_TQC2100_R01";
import { TQC2100_R01Filter, useTQC2100_R01Store } from "./Store_TQC2100_R01";
import { flattenGridRowsToDetail, pivotDetailToGridRows } from "./Util_TQC2100_R01";

interface Props {
	detailGridRef: React.RefObject<AgGridReact<TQC2100_R01DetailPivotDto> | null>;
	tabList: Tab[] | undefined;
	setMessage: (message: string) => void;
}

/** 조회, 저장, 출력 등 액션바에서 호출하는 화면 업무 로직 */
export const Content_TQC2100_R01 = ({ detailGridRef, tabList, setMessage }: Props) => {
	void detailGridRef;
	void tabList;
	const {
		TQC2100_R01Filter,
		TQC2100_R01DetailPivotList,
		editTQC2100_R01,
		selectedTQC2100_R01,
		detailFetchTrigger,
		setTQC2100_R01List,
		setTQC2100_R01PastFilter,
		setTQC2100_R01DetailPivotList,
		setOriginalTQC2100_R01DetailPivotList,
		setSelectedTQC2100_R01,
		setEditTQC2100_R01,
		setAllCount,
		setDetailFetchTrigger,
		initData,
	} = useTQC2100_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { printPdfFromBlob } = useQzPrint();
	const mastersRef = useRef<TQC2100_R01InspectMasterDto[]>([]);
	useFetchActionbarAuth("TQC2100_R01");

	const params = (filter: TQC2100_R01Filter) => ({
		compCd: filter.compCd || undefined,
		inspectDt: filter.inspectDt || undefined,
	});

	// 서버의 flat 상세를 월간 피벗 행으로 변환하고 현재값과 비교용 원본을 함께 저장한다.
	const applyRows = useCallback((flat: TQC2100_R01DetailDto[]) => {
		const rows = pivotDetailToGridRows(
			flat,
			mastersRef.current,
			TQC2100_R01Filter.inspectDt,
		);
		setTQC2100_R01DetailPivotList(rows);
		setOriginalTQC2100_R01DetailPivotList(rows);
	}, [TQC2100_R01Filter.inspectDt, setTQC2100_R01DetailPivotList, setOriginalTQC2100_R01DetailPivotList]);

	const fetchDetail = useCallback(async (inspectNo: string) => {
		const response = await getTQC2100_R01DetailList(inspectNo);
		applyRows(response.data.data ?? []);
	}, [applyRows]);

	/** 헤더, 전체 건수, 검사기준을 함께 조회해 화면을 초기 구성한다. */
	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const filter = TQC2100_R01Filter;
			const [listResponse, countResponse, masterResponse] = await Promise.all([
				getTQC2100_R01List("search", params(filter)),
				getTQC2100_R01AllCount(params(filter)),
				getTQC2100_R01InspectList(),
			]);
			const list = listResponse.data.data ?? [];
			mastersRef.current = masterResponse.data.data ?? [];
			setTQC2100_R01List(list);
			setAllCount(countResponse.data.data);
			setTQC2100_R01PastFilter(filter);
			if (list.length > 0) {
				setSelectedTQC2100_R01(list[0]);
				ShowSuccessFetch(
					setMessage,
					`조회가 완료되었습니다. (총 ${list.length}/${countResponse.data.data}건)`,
				);
			} else {
				setSelectedTQC2100_R01(undefined);
				applyRows([]);
				showWarning("데이터가 없습니다. 신규 등록이 가능합니다.");
			}
			return listResponse;
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2100_R01Filter, applyRows, setAllCount, setTQC2100_R01List, setTQC2100_R01PastFilter, setLoadingSpinner, setMessage, setSelectedTQC2100_R01]);

	const initContainerData = useCallback(() => {
		initData();
		setEditTQC2100_R01({ ...defaultTQC2100_R01HeaderDto });
		mastersRef.current = [];
	}, [initData, setEditTQC2100_R01]);

	/** 입력된 피벗 셀을 상세 목록으로 펼쳐 신규 등록 또는 수정 API를 호출한다. */
	const handleSaveClick = useCallback(async () => {
		if (!validateString(editTQC2100_R01?.inspectorEmp || "")) {
			showWarning("검사자를 입력하세요.");
			return;
		}
		const payload = flattenGridRowsToDetail(
			TQC2100_R01DetailPivotList,
			{
				inspectNo: editTQC2100_R01?.inspectNo || "",
				compCd: TQC2100_R01Filter.compCd,
				inspectorEmp: editTQC2100_R01?.inspectorEmp || "",
				inspectorEmpNm: editTQC2100_R01?.inspectorEmpNm || "",
				approvorEmpno: editTQC2100_R01?.approvorEmpno || "",
				approvorEmpNm: editTQC2100_R01?.approvorEmpNm || "",
				headerRemark: editTQC2100_R01?.remark || "",
			},
			TQC2100_R01Filter.inspectDt,
		);
		if (payload.length === 0) {
			showWarning("저장할 측정값이 없습니다.");
			return;
		}
		setLoadingSpinner(true);
		try {
			const response = editTQC2100_R01?.inspectNo
				? await patchTQC2100_R01(payload)
				: await postTQC2100_R01(payload);
			if (!response.data.successFlag) return;
			const data = response.data.data as {
				inspectResultHeaderDto?: TQC2100_R01HeaderDto;
			};
			const inspectNo = data.inspectResultHeaderDto?.inspectNo;
			showSuccess("측정결과 저장이 완료되었습니다.");
			if (inspectNo) {
				const savedHeader = {
					...defaultTQC2100_R01HeaderDto,
					...editTQC2100_R01,
					inspectNo,
				};
				setSelectedTQC2100_R01(savedHeader);
				setEditTQC2100_R01(savedHeader);
				await fetchDetail(inspectNo);
			}
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2100_R01DetailPivotList, TQC2100_R01Filter, editTQC2100_R01, fetchDetail, setEditTQC2100_R01, setLoadingSpinner, setSelectedTQC2100_R01]);

	const handlePrintClick = useCallback(async () => {
		const inspectNo = selectedTQC2100_R01?.inspectNo;
		if (!inspectNo) return showWarning("출력할 측정결과를 선택하세요.");
		const blob = await getPdfBlob_TQC2100_R01(inspectNo);
		// QZ 연결이 없거나 직접 인쇄에 실패하면 브라우저 PDF 창으로 대체한다.
		if (!qz.websocket.isActive() || !(await printPdfFromBlob(blob, "report1"))) {
			window.open(URL.createObjectURL(blob), "_blank");
		}
	}, [printPdfFromBlob, selectedTQC2100_R01]);

	const handleExcelClick = useCallback(async () => {
		const inspectNo = selectedTQC2100_R01?.inspectNo;
		if (!inspectNo) return showWarning("선택된 측정결과가 없습니다.");
		const response = await downloadExcel_TQC2100_R01(inspectNo);
		downloadSettingExcel({
			responseData: response.data.data,
			fileName: "마스터시편 측정등록-excel.xlsx",
		});
	}, [selectedTQC2100_R01]);

	useEffect(() => {
		// 헤더 선택으로 발생한 상세 조회 요청을 한 번 처리한 뒤 trigger를 해제한다.
		if (!detailFetchTrigger || !selectedTQC2100_R01?.inspectNo) return;
		fetchDetail(selectedTQC2100_R01.inspectNo);
		setDetailFetchTrigger(false);
	}, [detailFetchTrigger, fetchDetail, selectedTQC2100_R01, setDetailFetchTrigger]);

	return {
		fetchData,
		initContainerData,
		handleSaveClick,
		handlePrintClick,
		handleExcelClick,
		handleInsertClick: () =>
			showWarning("점검내용은 검사기준(MS+FPI) 기준으로 표시됩니다."),
		handleDeleteClick: () =>
			showWarning("일자별 측정값은 셀에서 직접 삭제하세요."),
	};
};
